import PropTypes from 'prop-types';
import React from 'react';
import { i18n } from '@international/mastodon-i18n';
import { M } from '@dashboard-experience/mastodon';
import T from 'components/T';

const Text = ({
  autoFocus,
  disabled,
  input,
  label,
  meta: { touched, error, warning },
  placeholder,
  type,
  onPaste,
  dateformat,
  testId,
}) => {
  // The field shows what was typed, not the value redux-form stores, so a
  // `normalize` (the SSN dashes, for one) changes the form value and not the
  // text in the box. That is how this field has always behaved.
  const [value, setValue] = React.useState('');
  const inputRef = React.useRef(null);
  const onChangeHandler = e => {
    setValue(e.target.value);
    input.onChange(e);
  };

  React.useEffect(() => {
    const initialValue = inputRef.current?.value;
    if (initialValue) setValue(initialValue);
  }, []);

  // A validation error is a translation key, or `{ message, ...params }` with
  // the params for that translation.
  const { message: errorKey, ...errorParams } =
    error && typeof error !== 'string' ? error : { message: error };
  const invalid = Boolean(touched && errorKey);
  const warn = Boolean(touched && !errorKey && warning);

  return (
    <M.TextInput
      id={input.name ? input.name.replace('.', '-') : 'input-name'}
      name={input.name}
      type={type || undefined}
      ref={inputRef}
      value={value}
      onChange={onChangeHandler}
      onBlur={event => input.onBlur(event)}
      onFocus={input.onFocus}
      labelText={label ? <T value={label} dateformat={dateformat} /> : ''}
      placeholder={i18n.getStr(placeholder || label)}
      autoFocus={autoFocus}
      disabled={disabled}
      onPaste={onPaste}
      data-testid={testId}
      invalid={invalid}
      invalidText={
        invalid ? <T value={errorKey} {...errorParams} /> : undefined
      }
      warn={warn}
      warnText={warn ? warning : undefined}
    />
  );
};

Text.propTypes = {
  autoFocus: PropTypes.bool,
  disabled: PropTypes.bool,
  input: PropTypes.object,
  label: PropTypes.string,
  meta: PropTypes.object,
  placeholder: PropTypes.string,
  onPaste: PropTypes.func,
  type: PropTypes.string,
  dateformat: PropTypes.string,
  testId: PropTypes.string,
};

Text.defaultProps = {
  autoFocus: false,
  disabled: false,
  input: {},
  label: '',
  meta: {},
  placeholder: '',
  onPaste: () => {},
  type: '',
  dateformat: '',
  testId: '',
};

export default i18n.renderTranslation()(Text);
